import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard, Users, CreditCard, BarChart3, Activity, Megaphone,
  FileText, Cpu, FileCode, Key, DollarSign, TrendingUp, AlertTriangle,
} from 'lucide-react'
import type { UserRole } from '@atsready/shared'
import { ROLE_HIERARCHY } from '@atsready/shared'

interface NavigationItem {
  name: string
  href: string
  icon: LucideIcon
  minRole: string
}

interface NavigationSection {
  title: string
  items: NavigationItem[]
}

export const ADMIN_ROUTE_PATHS = {
  dashboard: '/',
  users: '/users',
  userDetail: '/users/:id',
  subscriptions: '/subscriptions',
  campaigns: '/campaigns',
  planManagement: '/billing/plans',
  analytics: '/analytics',
  perplexityMetrics: '/perplexity-metrics',
  auditLogs: '/audit-logs',
  aiProviders: '/ai/providers',
  aiPrompts: '/ai/prompts',
  aiSecrets: '/ai/secrets',
  aiPricing: '/ai/pricing',
  aiUsage: '/ai/usage',
  aiCostLogErrors: '/ai/cost-log-errors',
  blog: '/blog',
  blogDetail: '/blog/:slug',
} as const

export const ADMIN_ROUTE_TEST_PATHS = {
  ...ADMIN_ROUTE_PATHS,
  userDetail: '/users/user_123',
  blogDetail: '/blog/upload-your-resume-what-happens-next',
} as const

export function toAdminUserDetailPath(id: string) {
  return ADMIN_ROUTE_PATHS.userDetail.replace(':id', id)
}

export function toAdminBlogDetailPath(slug: string) {
  return ADMIN_ROUTE_PATHS.blogDetail.replace(':slug', slug)
}

export const ADMIN_ROUTE_CATALOG = [
  { path: ADMIN_ROUTE_PATHS.dashboard, title: 'Dashboard', section: 'Overview', navGroup: 'Core', navLabel: 'Dashboard', minRole: 'SUPPORT', iconKey: 'dashboard', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.users, title: 'Users', section: 'Accounts', navGroup: 'Core', navLabel: 'Users', minRole: 'SUPPORT', iconKey: 'users', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.userDetail, pathMatcher: /^\/users\/[^/]+$/, title: 'User Detail', section: 'Accounts', navGroup: 'Core', navLabel: 'Users', minRole: 'SUPPORT', iconKey: 'users', includeInNav: false },
  { path: ADMIN_ROUTE_PATHS.subscriptions, title: 'Subscriptions', section: 'Accounts', navGroup: 'Core', navLabel: 'Subscriptions', minRole: 'SUPPORT', iconKey: 'credit-card', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.campaigns, title: 'Campaigns', section: 'Accounts', navGroup: 'Core', navLabel: 'Campaigns', minRole: 'ADMIN', iconKey: 'megaphone', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.planManagement, title: 'Plan Management', section: 'Operations', navGroup: 'Operations', navLabel: 'Plan Management', minRole: 'ADMIN', iconKey: 'credit-card', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.analytics, title: 'Analytics', section: 'Insights', navGroup: 'Insights', navLabel: 'Analytics', minRole: 'ANALYST', iconKey: 'bar-chart', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.perplexityMetrics, title: 'Perplexity Metrics', section: 'Insights', navGroup: 'Insights', navLabel: 'Perplexity Metrics', minRole: 'ANALYST', iconKey: 'activity', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.auditLogs, title: 'Audit Logs', section: 'Operations', navGroup: 'Operations', navLabel: 'Audit Logs', minRole: 'ADMIN', iconKey: 'file-text', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.aiProviders, title: 'AI Providers', section: 'AI Operations', navGroup: 'AI Operations', navLabel: 'AI Providers', minRole: 'ADMIN', iconKey: 'cpu', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.aiPrompts, title: 'AI Prompts', section: 'AI Operations', navGroup: 'AI Operations', navLabel: 'AI Prompts', minRole: 'ADMIN', iconKey: 'file-code', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.aiSecrets, title: 'AI Secrets', section: 'AI Operations', navGroup: 'AI Operations', navLabel: 'AI Secrets', minRole: 'ADMIN', iconKey: 'key', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.aiPricing, title: 'AI Pricing', section: 'AI Operations', navGroup: 'AI Operations', navLabel: 'AI Pricing', minRole: 'ADMIN', iconKey: 'dollar-sign', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.aiUsage, title: 'AI Usage', section: 'AI Operations', navGroup: 'AI Operations', navLabel: 'AI Usage', minRole: 'ANALYST', iconKey: 'trending-up', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.aiCostLogErrors, title: 'Cost Log Failures', section: 'Operations', navGroup: 'Operations', navLabel: 'Cost Log Failures', minRole: 'ANALYST', iconKey: 'alert-triangle', includeInNav: true },
  // Drafts are unpublished content, so this group sits at ADMIN rather than the
  // SUPPORT floor the Core group uses. The nav is decoration either way — the
  // API is the real gate — but a pre-publication draft should not be one
  // mistyped URL away from a support session.
  { path: ADMIN_ROUTE_PATHS.blog, title: 'Blog', section: 'Content', navGroup: 'Content', navLabel: 'Blog', minRole: 'ADMIN', iconKey: 'file-text', includeInNav: true },
  { path: ADMIN_ROUTE_PATHS.blogDetail, pathMatcher: /^\/blog\/[^/]+$/, title: 'Blog Post', section: 'Content', navGroup: 'Content', navLabel: 'Blog', minRole: 'ADMIN', iconKey: 'file-text', includeInNav: false },
] as const

export const ADMIN_ICON_MAP: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  users: Users,
  'credit-card': CreditCard,
  'bar-chart': BarChart3,
  activity: Activity,
  'file-text': FileText,
  cpu: Cpu,
  'file-code': FileCode,
  key: Key,
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'alert-triangle': AlertTriangle,
  megaphone: Megaphone,
}

export function getNavigationSectionsForRole(role: UserRole): NavigationSection[] {
  const allowedRoutes = ADMIN_ROUTE_CATALOG.filter((route) => {
    return route.includeInNav && ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[route.minRole as UserRole]
  })

  return allowedRoutes.reduce<NavigationSection[]>((sections, route) => {
    const existing = sections.find((section) => section.title === route.navGroup)
    const item: NavigationItem = {
      name: route.navLabel,
      href: route.path,
      icon: ADMIN_ICON_MAP[route.iconKey],
      minRole: route.minRole,
    }

    if (existing) {
      existing.items.push(item)
      return sections
    }

    return [...sections, { title: route.navGroup, items: [item] }]
  }, [])
}

export function getAdminPageMeta(pathname: string) {
  const matched = ADMIN_ROUTE_CATALOG.find((route) => {
    if ('pathMatcher' in route && route.pathMatcher) {
      return route.pathMatcher.test(pathname)
    }
    return route.path === pathname
  })

  if (matched) {
    return { title: matched.title, section: matched.section }
  }

  const leaf = pathname.split('/').filter(Boolean).at(-1) ?? 'dashboard'
  return {
    title: leaf
      .split('-')
      .map((part) => `${part[0].toUpperCase()}${part.slice(1)}`)
      .join(' '),
    section: 'Admin',
  }
}
